import { RunInContentAds } from '../enums/SiteOptions'
import { Device } from '../helpers'
import Lazyable from '../lazyable/Lazyable'
import PrebidFacade from '../prebidFacade/PrebidFacade'
import { Slot } from '../slots/Slot'
import SlotFactory from '../slots/SlotFactory'
import Logger from '../helpers/Logger'

export const DEFAULT_MAX_MOBILE_DENSITY = 0.3
export const DEFAULT_MIN_MOBILE_BUFFER = 2
export const DEFAULT_MAX_DESKTOP_DENSITY = 0.25
export const DEFAULT_MIN_DESKTOP_BUFFER = 2
export const AVG_AD_HEIGHT = 250 /* px */
export const MIN_TEXT_CONTENT_FOR_PARAGRAPH = 9
export const MIN_SLOT_LIMIT = 4
export const DENSITY_REDUCTION_RATE = 0.005
export const AVG_SLOT_HEIGHT = 250 /* px */
export const NON_TEXT_ELEMENTS = [
  'img',
  'iframe',
  'video',
  'embed',
  'object',
  'form'
]
export const EST_HEIGHT_TO_WIDTH_RATIO = 0.75

// We should only bust into these elements.
const elementsToBust = ['DIV', 'ARTICLE', 'SECTION', 'MAIN']
const elementPositionsToBust: Array<string | null> = ['relative', 'static']
const unbustableClasses = [
  '.mv-unbustable',
  '.wprm-recipe-container', // WPRM Container
  '.mv-create-card', // Create Card
  '.wp-block-mv-recipe', // Trellis Create Container
  'div[id^=om-][id$=-holder]' // Optin Monster Div
]

export interface IDynamicConfig {
  readonly slotClass: SlotClass
  target: Element
  slotFactory: SlotFactory
  prebidFacade: PrebidFacade
  divBuster: boolean

  slotLimit?: number
  density?: number
  minParagraphBuffer?: number
  startingParagraphNumber?: number
  endingParagraphNumber?: number
  contentRequireText?: RunInContentAds
  stopSelector?: string
  unbustableSelector?: string
}

export default class Dynamic {
  slotsInserted = 0
  slots: Array<Slot | undefined> = []
  private readonly slotClass: SlotClass
  private readonly prebidFacade: PrebidFacade
  private readonly slotFactory: SlotFactory
  private readonly minParagraphBuffer: number
  private readonly startingDensity: number
  private readonly elements: Array<HTMLElement>
  private readonly slotLimit: number | false
  private readonly contentRequireText: RunInContentAds
  private readonly stopSelector: string | undefined
  private readonly divBuster: boolean
  private readonly unbustableSelector: string
  private currentParagraphBuffer: number
  private pixelsToNextSlot = 0
  private slotCounter = 0
  private startingParagraphNumber = 0
  private leavesVisited = 0

  constructor(config: IDynamicConfig) {
    const defaultMinBuffer = Device.isMobileOrTablet
      ? DEFAULT_MIN_MOBILE_BUFFER
      : DEFAULT_MIN_DESKTOP_BUFFER

    const defaultDensity = Device.isMobileOrTablet
      ? DEFAULT_MAX_MOBILE_DENSITY
      : DEFAULT_MAX_DESKTOP_DENSITY

    this.divBuster = config.divBuster
    const allContentElements = getElements(config.target)
    const startingParagraphNumber = config.startingParagraphNumber || 0
    const endingParagraphNumber = config.endingParagraphNumber
      ? allContentElements.length - config.endingParagraphNumber
      : allContentElements.length

    this.startingParagraphNumber = startingParagraphNumber
    this.startingDensity = config.density || defaultDensity
    this.slotClass = config.slotClass
    this.prebidFacade = config.prebidFacade
    this.slotFactory = config.slotFactory
    this.slotLimit = getSlotLimit(config.slotLimit)
    // If we're div-busting, we should not alter the list of allElements
    this.elements = allContentElements.slice(
      this.divBuster ? 0 : startingParagraphNumber,
      endingParagraphNumber
    )
    this.stopSelector = config.stopSelector
    this.minParagraphBuffer = config.minParagraphBuffer || defaultMinBuffer
    this.contentRequireText = config.contentRequireText || 0
    this.currentParagraphBuffer = this.minParagraphBuffer
    // combine the config.unbustableSelector with the hardcoded unbustableClasses to make
    // a selector that matches unbustable elements.
    const unbustableArray = config.unbustableSelector
      ? [config.unbustableSelector]
      : []
    this.unbustableSelector = [...unbustableClasses, ...unbustableArray].join(
      ', '
    )
  }

  /**
   * This method is run recursively in "findDensityMode" one or more times
   * in order to find the best density.
   *
   * If the starting density results in a slot count lower than our slot limit,
   * it will call without findDensityMode and insert at that density.
   *
   * If we exceed our slot limit, it will recall in findDensityMode with an
   * incrementally lower density until the slot limit is not exceeded.
   *
   * The slots are always inserted on the tail call!
   */
  createSlots(
    density: number = this.startingDensity,
    findDensityMode = true
  ): number {
    this.slotCounter = 0
    this.leavesVisited = 0
    const pixelsBetweenSlots = parseInt(
      // @ts-ignore
      AVG_AD_HEIGHT / density - AVG_AD_HEIGHT,
      10
    )
    this.pixelsToNextSlot = pixelsBetweenSlots
    // # of elements required between each ad
    this.currentParagraphBuffer = this.minParagraphBuffer

    this.loopSlots(this.elements, findDensityMode, pixelsBetweenSlots)

    if (this.slotLimit && this.slotCounter > this.slotLimit) {
      // ran over limit, try again
      return this.createSlots(density - DENSITY_REDUCTION_RATE)
    }

    // Not over limit. Run the real thing.
    if (findDensityMode) {
      return this.createSlots(density, false)
    }

    // Finished.
    return density
  }

  loopSlots(
    elements: Array<HTMLElement>,
    findDensityMode: boolean,
    pixelsBetweenSlots: number
  ) {
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i]
      const nextElement = elements[i + 1]

      if (
        !element ||
        (this.stopSelector && element.matches(this.stopSelector))
      ) {
        // We hit our stop selector. We're done here. Go home.
        break
      }

      const children = Array.from(element.children) as HTMLElement[]
      const computedStyles = window.getComputedStyle(element)
      const diveIntoElement = this.doesBustinMakeMeFeelGood(
        element,
        pixelsBetweenSlots,
        computedStyles
      )

      if (diveIntoElement) {
        this.loopSlots(children, findDensityMode, pixelsBetweenSlots)
        continue
      }

      // Measure up the paragraph and remove that height from the distance
      // to the next slot.  Ignore `position: absolute` elements.
      const height =
        computedStyles.position !== 'absolute'
          ? measureElementHeight(element)
          : 0
      this.pixelsToNextSlot -= height

      if (isValidParagraph(element, height)) {
        this.currentParagraphBuffer += 1
        this.leavesVisited += 1
        if (this.divBuster && !findDensityMode) {
          Logger.debug('Buster Insert Point: ', this.leavesVisited, element)
        }
      }

      // See Dashboard > Settings > In-Content Ads > In-Content Placement Rules
      const satisfiesPlacementRules = checkPlacementRules(
        element,
        nextElement,
        this.contentRequireText
      )

      // Reset/Increment the counters if
      // Must be at or above distance from last slot
      // Must be a block
      // Must have minimum # of paragraphs between slots
      // And must satisfy the In Content Placement Rules
      // If we're not in find density mode, we actually insert the slot
      if (
        !isWprmCard(element) &&
        (this.leavesVisited >= this.startingParagraphNumber ||
          !this.divBuster) &&
        this.pixelsToNextSlot <= 0 &&
        isBlock(element) &&
        this.currentParagraphBuffer >= this.minParagraphBuffer &&
        satisfiesPlacementRules
      ) {
        this.slotCounter += 1
        this.currentParagraphBuffer = 0

        // Try to be lenient with the distance between slots, but don't be too lenient.
        // If this.pixelsToNextSlots is a very large negative number, we can get ourselves
        // in trouble with too many ads that are too close together.
        this.pixelsToNextSlot = Math.max(
          this.pixelsToNextSlot + pixelsBetweenSlots,
          100 // minimum pixels between slots
        )

        if (!findDensityMode) {
          Dynamic.insertSlot(
            element,
            this.slotClass,
            this.slotFactory,
            this.prebidFacade
          )
        }
      }
    }
  }

  /**
   * Should we just bust into an element?
   * There are bunch of rules we use to determine if the div buster should bust into an element.
   * Important: https://www.youtube.com/watch?v=ybcS7CP4Om0
   * @param element
   * @param pixelsBetweenSlots
   * @param computedStyles - To prevent recalculating the styles, you can pass them in.
   */
  doesBustinMakeMeFeelGood(
    element: HTMLElement,
    pixelsBetweenSlots: number,
    computedStyles?: CSSStyleDeclaration
  ): boolean {
    if (!this.divBuster) {
      return false
    }
    let elementHasEnoughHeightForTwoAds: boolean = false
    const parent = element.parentElement as HTMLElement
    const children = Array.from(element.children) as HTMLElement[] // getParagraphs(element)
    const elementStyles = computedStyles || window.getComputedStyle(element)

    const elementTagIsBustable = elementsToBust.indexOf(element.tagName) !== -1
    const elementPositionIsGood =
      elementPositionsToBust.indexOf(elementStyles.position) !== -1
    const elementHasChildren = children.length > 0

    // We shouldn't bust if the element isn't about the same width as it's parent.
    const minBustingWidthRatio = 0.8
    const elementIsWideEnough =
      element.offsetWidth / parent.offsetWidth > minBustingWidthRatio

    // Make sure the element hasn't been declared unbustable
    const elementIsNotUnbustable = !element.matches(this.unbustableSelector)

    let diveIntoElement =
      elementHasChildren &&
      elementTagIsBustable &&
      elementIsWideEnough &&
      elementPositionIsGood &&
      elementIsNotUnbustable &&
      (isBlock(element) || elementStyles.display === 'flex')

    // This is a more computationally expensive check, so we only do it if
    // all the other checks have passed.
    if (diveIntoElement) {
      elementHasEnoughHeightForTwoAds =
        this.pixelsToNextSlot + pixelsBetweenSlots <
        measureElementHeight(element)

      diveIntoElement = diveIntoElement && elementHasEnoughHeightForTwoAds
    }

    // This will be helpful for debugging in the future:
    // console.log(
    //   'elementTagIsBustable: ',
    //   elementTagIsBustable,
    //   'elementIsBlock',
    //   (isBlock(element) || elementStyles.display === 'flex'),
    //   'elementPositionIsGood',
    //   elementPositionIsGood,
    //   'elementHasChildren',
    //   elementHasChildren,
    //   'elementHasEnoughHeightForAd',
    //   elementHasEnoughHeightForTwoAds,
    //   'elementIsWideEnough',
    //   elementIsWideEnough,
    //   element.offsetWidth,
    //   parent.offsetWidth,
    //   'pixelsToNextSlot',
    //   this.pixelsToNextSlot,
    //   measureElementHeight(element),
    //   diveIntoElement,
    //   element.tagName,
    //   element
    // )

    return diveIntoElement
  }

  /**
   * Creates an ad hoc slot model and instantiates it.
   * If its a lazy slot, a Lazyable is instantiated with the slot.
   * Else, bids are immediately requested on it.
   * @param target
   */
  static insertSlot(
    target: HTMLElement,
    slotClass: SlotClass,
    slotFactory: SlotFactory,
    prebidFacade: PrebidFacade
  ): Slot | null {
    // use given slot model with dynamic target
    const model: ISlotModel = {
      type: slotClass,
      selector: target,
      insertPosition: 'afterend',
      adUnitId: 'dynamic'
    }

    const slot = slotFactory.new(model)
    if (!slot) {
      return null
    }

    if (slot && slot.lazy) {
      new Lazyable(slot, prebidFacade)
    }

    return slot
  }
}

export function isElementContainingText(el: HTMLElement | null): boolean {
  return (
    !!el &&
    !!el.textContent &&
    el.textContent.length >= MIN_TEXT_CONTENT_FOR_PARAGRAPH
  )
}

export function isSlot(el: HTMLElement): boolean {
  return !!el.querySelector('.adunit')
}

/**
 * Returns el if it is a or contains a non-text element
 */
export function getNonTextElementOrNull(el: HTMLElement): HTMLElement | null {
  return NON_TEXT_ELEMENTS.indexOf(el.nodeName.toLowerCase()) !== -1
    ? (el as HTMLElement)
    : (el.querySelector(NON_TEXT_ELEMENTS.join()) as HTMLElement | null)
}

/**
 * Returns first truthy width multiplied by EST_HEIGHT_TO_WIDTH_RATIO
 */
export function getEstimatedHeightFromElementWidth(
  image: HTMLElement,
  container: HTMLElement
): number {
  const parentNode = image.parentNode as HTMLElement

  // If the <img> tag itself has height and width attributes set, use that ratio.
  const imageHeightAttrib = parseFloat(image.getAttribute('height') || '0')
  const imageWidthAttrib = parseFloat(image.getAttribute('width') || '1')

  // image will not have 'src' if it is being lazy loaded, and has not yet been been chosen to load.
  const imageHasSrc = !!image.getAttribute('src')

  const imageRatio =
    imageHeightAttrib / imageWidthAttrib || EST_HEIGHT_TO_WIDTH_RATIO

  // image.offsetHeight has to be >100 because unloaded images will have the height/width of their alt text.
  // Hopefully the alt text won't be larger than 100px.
  const probablyNotAltText = image.offsetHeight > 100

  const imageWidth = imageHasSrc && probablyNotAltText ? image.offsetWidth : 0
  const containerWidth = container && container.offsetWidth
  const parentNodeWidth = parentNode && parentNode.offsetWidth

  const width = imageWidth || containerWidth || parentNodeWidth

  const value = width * imageRatio

  return value ? value : 0 // guard for NaN
}

export function imageHasNotRendered(image: HTMLElement): boolean {
  const isProbablyLazyAndUnloaded =
    (image.getAttribute('data-lazy-src') || // Check for JetPack lazy image
      image.getAttribute('data-lazy-type') === 'image') && // Check for A3 Lazy Image: https://wordpress.org/plugins/a3-lazy-load/
    (!image.getAttribute('src') || image.offsetHeight === 1)

  return (
    isProbablyLazyAndUnloaded ||
    (image.offsetHeight === 0 &&
      (image.style.display !== 'none' || image.getAttribute('srcset') !== null))
  )
}

/** Returns truthy if BR tag || a block element && NOT a h1-9 */
export function isBlock(el: HTMLElement): boolean {
  return (
    (window.getComputedStyle(el).display === 'block' &&
      !el.nodeName.match(/H[1-9]/)) ||
    el.nodeName === 'BR'
  )
}

/** Returns HTMLElement's height OR estimated height if it is or contains an image */
export function measureElementHeight(element: HTMLElement): number {
  let height = element.offsetHeight
  const nonTextElement = getNonTextElementOrNull(element)

  if (nonTextElement && imageHasNotRendered(nonTextElement)) {
    height += getEstimatedHeightFromElementWidth(nonTextElement, element)
  }

  // Check if we're a hidden element. Bloggers do this for Pintrest.
  if (element.style.display === 'none') {
    height = 0
  }

  return height
}

export function isValidParagraph(
  element: HTMLElement,
  height?: number
): boolean {
  return (
    !isSlot(element) &&
    (getNonTextElementOrNull(element) || isElementContainingText(element)) &&
    (height || measureElementHeight(element)) > 0
  )
}

export const isWprmCard = (element: HTMLElement): boolean =>
  Array.from(element.classList).some((klass) => klass.indexOf('wprm') !== -1)

/** Validates in-content rules in dashboard
 * @see [Dashboard settings page](https://dashboard.mediavine.com/sites/38/edit)
 */
export function checkPlacementRules(
  element: HTMLElement,
  nextElement: HTMLElement,
  runInContentAds: RunInContentAds
): boolean {
  let hasValidContent = true
  const nonTextElement = getNonTextElementOrNull(element)
  if (runInContentAds === RunInContentAds.onlyAfterTextParagraphs) {
    hasValidContent = !nonTextElement && isElementContainingText(element)
  }

  if (runInContentAds === RunInContentAds.onlyBetweenTextParagraphs) {
    hasValidContent =
      !nonTextElement &&
      isElementContainingText(element) &&
      isElementContainingText(nextElement) &&
      !getNonTextElementOrNull(nextElement)
  }

  return hasValidContent
}

/** Given an HTMLElement, recurses child elements until a collection > 1
 * is found OR returns empty array
 */
export function getElements(el: Element): Array<HTMLElement> {
  let elements = el && el.children
  // 2) In case all of content wrapped in div such as Thrive / Theia, grab the first child node containing children
  while (
    elements &&
    elements.length === 1 &&
    elements[0].children &&
    elements[0].children.length > 0
  ) {
    elements = elements[0].children
  }

  if (!elements) {
    return []
  }

  return Array.from(elements) as Array<HTMLElement>
}

export function getSlotLimit(slotLimit?: number): number | false {
  return slotLimit && slotLimit >= MIN_SLOT_LIMIT ? slotLimit : false
}
